/**
 * CreateGroupModal - Modal for creating a new trading group
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  Pressable,
  TextInput,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { Button } from '@/components/common/Button';
import { createGroup } from '@/services/groups/groupService';

interface CreateGroupModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: (groupId: string, groupName: string) => void;
}

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const inputRef = useRef<TextInput>(null);

  // Auto-focus input when modal opens
  useEffect(() => {
    if (visible) {
      setTimeout(() => inputRef.current?.focus(), 300);
    } else {
      // Reset on close
      setGroupName('');
      setError('');
    }
  }, [visible]);

  const handleCreate = async () => {
    const trimmedName = groupName.trim();

    if (!trimmedName) {
      setError('Please enter a group name');
      return;
    }

    if (trimmedName.length < 3) {
      setError('Group name must be at least 3 characters');
      return;
    }

    if (trimmedName.length > 50) {
      setError('Group name must be less than 50 characters');
      return;
    }

    setLoading(true);
    setError('');

    const { data, error: createError } = await createGroup(trimmedName);

    setLoading(false);

    if (data) {
      onSuccess(data.id, data.name);
      onClose();
    } else {
      setError(createError || 'Failed to create group');
    }
  };

  const handleClose = () => {
    setGroupName('');
    setError('');
    onClose();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.container}
      >
        <View style={styles.overlay}>
          <Pressable
            onPress={handleClose}
            style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
          />
          <View style={styles.modal} onStartShouldSetResponder={() => true}>
            {/* Header */}
            <View style={styles.header}>
              <View style={styles.headerLeft}>
                <View style={styles.iconCircle}>
                  <Ionicons name="add-circle" size={24} color={Colors.accent.primary} />
                </View>
                <Text style={styles.title}>Create Group</Text>
              </View>
              <Pressable onPress={handleClose} style={styles.closeButton} hitSlop={8}>
                <Ionicons name="close" size={24} color={Colors.text.secondary} />
              </Pressable>
            </View>

            {/* Content */}
            <View style={styles.content}>
              <Text style={styles.description}>
                Choose a name for your trading group. You can change this later.
              </Text>

              {/* Group Name Input */}
              <View style={styles.inputContainer}>
                <Text style={styles.inputLabel}>Group Name</Text>
                <TextInput
                  ref={inputRef}
                  style={styles.input}
                  value={groupName}
                  onChangeText={text => {
                    setGroupName(text);
                    setError('');
                  }}
                  placeholder="e.g., Trading Squad"
                  placeholderTextColor={Colors.text.tertiary}
                  maxLength={50}
                  editable={!loading}
                  autoCapitalize="words"
                  autoComplete="off"
                  autoCorrect={false}
                  returnKeyType="done"
                  onSubmitEditing={handleCreate}
                />
                <Text style={styles.charCount}>{groupName.length}/50 characters</Text>
              </View>

              {/* Error */}
              {error && (
                <View style={styles.errorContainer}>
                  <Ionicons name="alert-circle" size={16} color={Colors.status.error} />
                  <Text style={styles.errorText}>{error}</Text>
                </View>
              )}

              {/* Info Tip */}
              <View style={styles.tipContainer}>
                <Ionicons
                  name="information-circle-outline"
                  size={16}
                  color={Colors.accent.primary}
                />
                <Text style={styles.tipText}>
                  You'll receive an invite code after creating the group to share with others
                </Text>
              </View>
            </View>

            {/* Footer */}
            <View style={styles.footer}>
              <Button
                title={loading ? 'Creating...' : 'Create Group'}
                onPress={handleCreate}
                variant="primary"
                disabled={loading || !groupName.trim()}
                loading={loading}
                fullWidth
              />
              <Pressable onPress={handleClose} style={styles.cancelButton} disabled={loading}>
                <Text style={styles.cancelText}>Cancel</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modal: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 20,
    width: '100%',
    maxWidth: 420,
    borderWidth: 1,
    borderColor: Colors.border.default,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.5,
    shadowRadius: 20,
    elevation: 10,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  iconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: `${Colors.accent.primary}20`,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  closeButton: {
    padding: 8,
    borderRadius: 8,
  },
  content: {
    padding: 24,
  },
  description: {
    fontSize: 14,
    color: Colors.text.secondary,
    marginBottom: 24,
    lineHeight: 20,
    textAlign: 'center',
  },
  inputContainer: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 8,
  },
  input: {
    backgroundColor: Colors.background.tertiary,
    borderWidth: 2,
    borderColor: Colors.border.default,
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    color: Colors.text.primary,
    fontWeight: '500',
  },
  charCount: {
    fontSize: 12,
    color: Colors.text.tertiary,
    textAlign: 'right',
    marginTop: 4,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: `${Colors.status.error}15`,
    padding: 12,
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: Colors.status.error,
    marginBottom: 16,
  },
  errorText: {
    flex: 1,
    color: Colors.status.error,
    fontSize: 14,
  },
  tipContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: `${Colors.accent.primary}10`,
    padding: 12,
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: Colors.accent.primary,
  },
  tipText: {
    flex: 1,
    fontSize: 12,
    color: Colors.text.secondary,
    lineHeight: 16,
  },
  footer: {
    padding: 24,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: Colors.border.subtle,
  },
  cancelButton: {
    paddingVertical: 12,
    alignItems: 'center',
    marginTop: 8,
  },
  cancelText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text.secondary,
  },
});
